/**
 * The requirements that link to this element - issue #48.
 *
 * A link is STORED ON THE REQUIREMENT, which is the profile's one-end rule: a
 * part, a state, an interface is a sink, and every relationship pointing at one
 * is editable from exactly one end. So this is where that end becomes visible
 * from the other: which requirements point here, through which link, and a way
 * to add one - which writes the requirement's own field, exactly as its form
 * would.
 *
 * Only requirements whose TYPE declares the link are offered. Binding it on any
 * other would state a feature nothing declares, which the model reports as a
 * finding rather than a link.
 */

import { useCallback, useEffect, useState } from 'react'
import AddLinkIcon from '@mui/icons-material/AddLink'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { ApiError, api } from '../../api'
import type { LinkReport } from '../../schema'
import { useCanWrite, useSchema } from '../../state/AppData'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { useElements } from '../common/elements'

export default function LinkedRequirements({
  kind,
  identifier,
  title = 'Requirements',
}: {
  /** The element's kind, by name. */
  kind: string
  identifier: string
  title?: string
}) {
  const { allowed, reason } = useCanWrite()
  const [report, setReport] = useState<LinkReport | null>(null)
  const [failure, setFailure] = useState('')
  const [linking, setLinking] = useState(false)

  const load = useCallback(() => {
    api.links(kind, identifier)
      .then(setReport)
      .catch((error: ApiError) => setFailure(error.message))
  }, [kind, identifier])

  useEffect(load, [load])

  const unlink = (requirement: string, field: string) => {
    setFailure('')
    // EMPTY, not null: a PATCH skips a null as "not sent", and '' is how any
    // field is cleared - the binding is removed from the requirement.
    api.update('requirements', requirement, { [field]: '' })
      .then(load)
      .catch((error: ApiError) => setFailure(error.message))
  }

  const offerable = (report?.links ?? []).filter((link) => link.requirements.length > 0)

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {failure && <Alert severity="error">{failure}</Alert>}
      {!report ? null : report.requirements.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No requirement links here.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {report.requirements.map((entry) => (
            <Stack key={`${entry.id}-${entry.feature}`} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Link component={RouterLink} to={`/requirements/${encodeURIComponent(entry.id)}`}
                sx={{ fontFamily: MONOSPACE }}>
                {entry.id}
              </Link>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap title={entry.statement}>
                {entry.statement || entry.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.label}
              </Typography>
              <Tooltip title={allowed ? '' : reason} describeChild>
                <span>
                  <IconButton size="small" aria-label={`Unlink ${entry.id}`} disabled={!allowed}
                    onClick={() => unlink(entry.id, entry.field)}>
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
      {report && (
        <Tooltip
          title={!allowed ? reason
            : offerable.length === 0 ? 'No requirement type declares a link to this kind of element.' : ''}
          describeChild
        >
          <span>
            <Button size="small" startIcon={<AddLinkIcon />} disabled={!allowed || offerable.length === 0}
              onClick={() => setLinking(true)}>
              Link a requirement
            </Button>
          </span>
        </Tooltip>
      )}
      {linking && report && (
        <LinkDialog
          report={report}
          offerable={offerable}
          onClose={() => setLinking(false)}
          onLinked={() => {
            setLinking(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

function LinkDialog({
  report,
  offerable,
  onClose,
  onLinked,
}: {
  report: LinkReport
  offerable: LinkReport['links']
  onClose: () => void
  onLinked: () => void
}) {
  const schema = useSchema()
  const requirements = useElements(schema, 'requirements')
  const [feature, setFeature] = useState(offerable[0]?.feature ?? '')
  const [requirement, setRequirement] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const link = offerable.find((entry) => entry.feature === feature)
  const eligible = new Set(link?.requirements ?? [])

  const save = () => {
    if (!link) return
    setBusy(true)
    setFailure('')
    api.update('requirements', requirement, { [link.field]: report.identifier })
      .then(onLinked)
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link a requirement to {report.identifier}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          {offerable.length > 1 && (
            <TextField select size="small" label="Through" value={feature}
              onChange={(event) => {
                setFeature(event.target.value)
                setRequirement('')
              }}
              helperText="Which link of the requirement's type this is">
              {offerable.map((entry) => (
                <MenuItem key={entry.feature} value={entry.feature}>
                  {entry.label} ({entry.types.join(', ')})
                </MenuItem>
              ))}
            </TextField>
          )}
          <ElementPicker
            rows={requirements.rows}
            where={(row) => eligible.has(row.id)}
            value={requirement || null}
            onChange={setRequirement}
            label="Requirement"
            required
            helperText={link ? `Requirements of ${link.types.join(', ')}, which declare ${link.label}` : ''}
          />
          <Typography variant="caption" color="text.secondary">
            Written on the requirement: its {link?.label ?? 'link'} names {report.identifier}. A
            requirement links to one element per link, so one already linked elsewhere moves here.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !requirement || !link}>
          {busy ? 'Linking…' : 'Link'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
